import { useCallback, useMemo } from 'react'

import permissionService from '@/core/api/services/permissionService'
import { useAuth } from '@/core/hooks/useAuth'

/**
 * Reseller feature flags organized by domain
 */
export interface ResellerFeatures {
	billing: {
		enabled: boolean
	}
	zone: {
		create: boolean
		edit: boolean
		delete: boolean
		refresh: boolean
		viewPlaylists: boolean
		installationInfo: boolean
	}
	location: {
		edit: boolean
		delete: boolean
		share: boolean
	}
	group: {
		enabled: boolean
	}
	campaign: {
		create: boolean
		createAi: boolean
		edit: boolean
		delete: boolean
	}
	integrations: {
		enabled: boolean
	}
	account: {
		musicLegalCompliance: boolean
	}
}

export const usePermission = () => {
	const { user } = useAuth()

	const hasPermission = useCallback(
		(permission: string) => {
			return permissionService.hasPermission(user, permission)
		},
		[user]
	)

	const hasAllPermissions = useCallback(
		(permissions: string[]) => {
			return permissionService.hasAllPermissions(user, permissions)
		},
		[user]
	)

	const hasAnyPermission = useCallback(
		(permissions: string[]) => {
			return permissionService.hasAnyPermission(user, permissions)
		},
		[user]
	)

	const hasAccountSelectorAccess = useCallback(() => {
		return permissionService.canAccessAccountSelector(user)
	}, [user])

	// Build reseller feature flags object with explicit false checks (defaults to enabled when undefined)
	const features = useMemo<ResellerFeatures>(() => {
		const settings = user?.resellerSettings?.myBrandtrack

		return {
			billing: {
				// Show billing for non-subaccounts (direct customers) even if the reseller
				// has disabled it — the billing gate only applies to subaccounts.
				enabled:
					user?.isResellerSubaccount === false
						? true
						: (user?.resellerSettings?.isBillingEnabled ?? true),
			},
			zone: {
				create: settings?.zoneCreateEnabled !== false,
				edit: settings?.zoneEditEnabled !== false,
				delete: settings?.zoneDeleteEnabled !== false,
				refresh: settings?.zoneRefreshEnabled !== false,
				viewPlaylists: settings?.zoneViewPlaylistsEnabled !== false,
				installationInfo: settings?.zoneInstallationInfoEnabled !== false,
			},
			location: {
				edit: settings?.locationEditEnabled !== false,
				delete: settings?.locationDeleteEnabled !== false,
				share: settings?.locationShareEnabled !== false,
			},
			group: {
				enabled: settings?.groupFeatureEnabled !== false,
			},
			campaign: {
				create: settings?.campaignCreateEnabled !== false,
				createAi: settings?.campaignCreateAiEnabled !== false,
				edit: settings?.campaignEditEnabled !== false,
				delete: settings?.campaignDeleteEnabled !== false,
			},
			integrations: {
				enabled: settings?.integrationsFeatureEnabled !== false,
			},
			account: {
				musicLegalCompliance:
					settings?.accountMusicLegalComplianceEnabled !== false,
			},
		}
	}, [user?.resellerSettings, user?.isResellerSubaccount])

	return {
		hasPermission,
		hasAllPermissions,
		hasAnyPermission,
		resellerSettings: user?.resellerSettings,
		features,
		// Deprecated: kept for backward compatibility, use features object instead
		isBillingEnabled: features.billing.enabled,
		hasAccountSelectorAccess,
	}
}
